import React, { type CSSProperties, type ReactNode } from "react";

import clsx from "clsx";
import Interweave from "interweave";

import InfoAtomIcon from "@assets/info-atom-icon.svg";
import { useIsOnMmmPage } from "@hooks/use-is-on-mmm-page";
import { logEvent } from "@internal/analytics";
import { type FloatingPosition, HoverCard } from "@mantine/core";

interface TooltipProps {
  overlayOverCircleOnly?: boolean;
  noInfoCircle?: boolean;
  Wrapper?: (props: {
    children?: React.ReactNode | React.ReactNode[] | undefined;
  }) => JSX.Element;
  mouseLeaveDelay?: number;
  mouseEnterDelay?: number;
  className?: string;
}

export function HTMLTooltip({
  html,
  children,
  ...props
}: {
  html: string;
  children?: React.ReactNode | React.ReactNode[];
} & TooltipProps) {
  return (
    <GenericTooltip
      content={<Interweave content={html} />}
      // eslint-disable-next-line react/no-children-prop
      children={children}
      {...props}
    />
  );
}

export type GenericTooltipProps = {
  placement?: FloatingPosition;
  content: ReactNode;
  children?: React.ReactNode | React.ReactNode[];
  captureEventString?: string;
  overlayClassName?: string;
  infoCircleClassName?: string;
  arrowClassName?: string;
  destroyTooltipOnHide?: boolean;
  customIcon?: ReactNode;
  hideArrow?: boolean;
  showTooltip?: boolean;
  childrenContainerRef?: React.RefObject<HTMLDivElement> | null;
  style?: CSSProperties;
  v2Styling?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
} & TooltipProps;

export function GenericTooltip({
  content,
  children,
  Wrapper,
  overlayClassName,
  arrowClassName,
  noInfoCircle,
  mouseLeaveDelay,
  mouseEnterDelay,
  captureEventString,
  className,
  placement,
  infoCircleClassName,
  destroyTooltipOnHide = true,
  hideArrow = false,
  showTooltip = true,
  customIcon,
  v2Styling = false,
  style,
  onOpen,
  onClose,
}: GenericTooltipProps) {
  let inner = children;
  if (!noInfoCircle) {
    inner = (
      <>
        {children}
        <InfoCircle
          className={infoCircleClassName}
          customIcon={customIcon}
          v2Styling={v2Styling}
        />
      </>
    );
  }

  let body = (
    <span
      className={clsx(className, "nb-tooltip-children", {
        flex: !!customIcon,
      })}
      style={style}
    >
      {inner}
    </span>
  );

  if (Wrapper) {
    body = Wrapper({ children: inner });
  }

  if (!showTooltip || !content) {
    return body;
  }

  return (
    <HoverCard
      zIndex={9999}
      withArrow={!hideArrow}
      portalProps={{ target: document.body }}
      withinPortal
      arrowSize={8}
      shadow="xl"
      position={placement}
      closeDelay={mouseLeaveDelay ?? 100}
      openDelay={mouseEnterDelay ?? 100}
      keepMounted={!destroyTooltipOnHide}
      classNames={{ arrow: arrowClassName }}
      onClose={() => {
        onClose?.();
      }}
      onOpen={() => {
        onOpen?.();
        if (captureEventString) {
          logEvent(captureEventString);
        }
      }}
    >
      <HoverCard.Target>{body}</HoverCard.Target>
      <HoverCard.Dropdown
        className={clsx(overlayClassName, "text-xs rounded-[8px]")}
      >
        {content}
      </HoverCard.Dropdown>
    </HoverCard>
  );
}

export const Tooltip = GenericTooltip;

export function InfoCircle({
  className,
  customIcon,
  v2Styling = false,
}: {
  className?: string;
  customIcon?: ReactNode;
  v2Styling?: boolean;
}) {
  const isOnMmmPage = useIsOnMmmPage();

  if (customIcon) {
    return <span className={clsx("ml-1 z-2", className)}> {customIcon}</span>;
  } else if (isOnMmmPage || v2Styling) {
    return (
      <img
        className={clsx("ml-1 z-2 inline-block ", className)}
        src={InfoAtomIcon}
        alt="info"
      />
    );
  }

  return (
    <i
      className={clsx(
        "ml-1 fa-regular fa-sharp fa-info-circle text-gray z-2 opacity-90 text-xs",
        className,
      )}
    />
  );
}
